import { Box, Stack, Typography, Avatar, alpha, useTheme } from "@mui/material";
import { fPercent } from "src/utils/format-number";
import useLocales from "src/locales/use-locales";

interface Competitor {
    domain_name: string;
    total_rate: number;
}

interface CompetitorsTooltipProps {
    title: string;
    competitors?: Competitor[];
    onMouseLeave?: () => void;
}

export default function CompetitorsTooltip({ competitors = [], onMouseLeave = () => {}, title }: CompetitorsTooltipProps) {
    const theme = useTheme();
    const { t } = useLocales();
    const sortedCompetitors = [...competitors].filter((competitor: any) => competitor.rate_field > 0).sort((a: any, b: any) => b.rate_field - a.rate_field);

    return (
        <Box
            sx={{
                backgroundColor: "#FFFFFF",
                border: `1px solid rgba(0, 0, 0, 0.12)`,
                borderRadius: '12px',
                boxShadow: '0px 2px 8px 0px rgba(0, 0, 0, 0.1)',
                overflow: 'hidden',
                minWidth: 200,
            }}
            onMouseLeave={onMouseLeave}
        >
            {/* Header */}
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={1}
                sx={{
                    px: 1.5,
                    py: 1,
                    backgroundColor: "rgba(226, 229, 233, 0.16)",
                }}
            >
                <Typography
                    variant="body2"
                    sx={{
                        fontWeight: 700,
                        color: "#212B36",
                        fontSize: '14px',
                        lineHeight: '24px',
                    }}
                >
                    {t("dashboard.nonBrand.competitors")}
                </Typography>
                <Typography
                    variant="body2"
                    sx={{
                        fontWeight: 400,
                        color: "#637381",
                        fontSize: '14px',
                        lineHeight: '22px',
                        textAlign: 'right',
                    }}
                >
                    {title}
                </Typography>
            </Stack>

            {/* Competitors List */}
            <Stack spacing={0}>
                {sortedCompetitors.map((competitor, index) => (
                    <Stack
                        key={index}
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={1}
                        sx={{
                            px: 1.5,
                            py: 1,
                            backgroundColor: index === 0 
                                ? alpha("#919EAB", 0.1)
                                : 'transparent',
                            borderRadius: index === 0 ? '6px' : 0,
                            mx: 0.75,
                            mt: index === 0 ? 0.75 : 0,
                        }}
                    >
                        {/* Competitor Info */}
                        <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                            sx={{ flex: 1, minWidth: 0 }}
                        >
                            <Avatar
                                alt={competitor.domain_name}
                                src={`https://www.google.com/s2/favicons?domain=${competitor.domain_name}&sz=32`}
                                sx={{
                                    width: 18,
                                    height: 18,
                                    borderRadius: '12px',
                                }}
                            />
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 500,
                                    color: "#212B36",
                                    fontSize: '14px',
                                    lineHeight: '22px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {competitor.domain_name}
                            </Typography>
                        </Stack>

                        {/* Visibility Rate */}
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: 400,
                                color: "#637381",
                                fontSize: '14px',
                                lineHeight: '22px',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {fPercent((competitor as any).rate_field)}
                        </Typography>
                    </Stack>
                ))}
            </Stack>
        </Box>
    );
}